import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { styled, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ApartmentIcon from '@mui/icons-material/Apartment';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import { toast } from 'react-toastify';
import moment from 'moment';
import { getLeaveForSelectedMonth } from 'src/services/yearPlannerService';
import { HolidayType } from './enum/holydayTypes';
import { Holiday } from 'src/models/holyday';


const COMPANY_COLOR = "#00AEEF";
const holidayColors: Record<string, string> = {
  [HolidayType.POYA_DAY]: '#388e3c',
  [HolidayType.MERCANTILE_HOLIDAY]: '#fc585c',
  [HolidayType.EVENING_HALFDAY]: COMPANY_COLOR,
  [HolidayType.MORNING_HALFDAY]: COMPANY_COLOR,
  [HolidayType.PUBLIC_HOLIDAY]: '#1976d2',
  [HolidayType.COMPANY_HOLIDAY]: COMPANY_COLOR,
  [HolidayType.BANK_HOLIDAY]: '#f57c00',
};

const getActiveLegends = (holidays: Holiday[]) => {
  const presentTypes = new Set(
    holidays.map((h) => {
      if (
         // dayType: 1 = Morning Half Holiday, 2 = Evening Half Holiday, 0 = Full Day Holiday
        (h.dayType === 1 || h.dayType === 2) &&
        h.calenderType === HolidayType.COMPANY_HOLIDAY
      ) {
        return HolidayType.COMPANY_HOLIDAY;
      }
      return h.calenderType;
    })
  );
  const legendDefs = [
    {
      type: HolidayType.POYA_DAY,
      label: HolidayType.POYA_DAY,
      color: holidayColors[HolidayType.POYA_DAY]
    },
    {
      type: HolidayType.MERCANTILE_HOLIDAY,
      label: HolidayType.MERCANTILE_HOLIDAY,
      color: holidayColors[HolidayType.MERCANTILE_HOLIDAY]
    },
    {
      type: HolidayType.COMPANY_HOLIDAY,
      label: HolidayType.COMPANY_HOLIDAY,
      color: holidayColors[HolidayType.COMPANY_HOLIDAY]
    },
    {
      type: HolidayType.PUBLIC_HOLIDAY,
      label: HolidayType.PUBLIC_HOLIDAY,
      color: holidayColors[HolidayType.PUBLIC_HOLIDAY]
    },
    {
      type: HolidayType.BANK_HOLIDAY,
      label: HolidayType.BANK_HOLIDAY,
      color: holidayColors[HolidayType.BANK_HOLIDAY]
    }
  ];
  return legendDefs.filter((legendItem) => presentTypes.has(legendItem.type));
};

const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate();

const getFirstDayOfWeek = (year: number, month: number) => {
  return new Date(year, month, 1).getDay();
};

const getHolidaysForDate = (date: string, holidays: Holiday[]) =>
  holidays.filter((h) => h.eventDate.substring(0, 10) === date);

const formatDate = (year: number, month: number, day: number) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;

const isToday = (year: number, month: number, day: number) => {
  const today = new Date();
  return (
    today.getFullYear() === year &&
    today.getMonth() === month &&
    today.getDate() === day
  );
};

const CalendarRoot = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0
}));

const CalendarTable = styled('table')(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderCollapse: 'separate',
  borderSpacing: '4px',
  tableLayout: 'fixed'
}));

const CalendarCell = styled('td')<{ isToday?: boolean }>(
  ({ theme, isToday }) => ({
    aspectRatio: '1/1',
    minWidth: 0,
    textAlign: 'center',
    verticalAlign: 'top',
    background: isToday ? '#e3f2fd' : 'transparent',
    position: 'relative',
    padding: 0,
    cursor: 'pointer',
    '&:hover': {
      background: isToday ? '#bbdefb' : '#f5f5f5'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: 18,
      minWidth: 0,
      minHeight: 0,
      padding: 0
    }
  })
);

const HalfdayDiv = styled('div')<{
  color: string;
  half: 'morning' | 'evening';
}>(({ color, half }) => ({
  position: 'absolute',
  top: half === 'morning' ? 0 : '50%',
  left: 0,
  width: '100%',
  height: '50%',
  background: color,
  opacity: 0.6,
  borderRadius: half === 'morning' ? '6px 6px 0 0' : '0 0 6px 6px',
  pointerEvents: 'none'
}));

const Legend = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  justifyContent: 'center',
  margin: `${theme.spacing(1)} 0`,
  flexWrap: 'wrap',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    margin: `${theme.spacing(0.5)} 0`
  }
}));

const Calendar = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [month, setMonth] = useState<number>(today.getMonth());
  const [modalHolidays, setModalHolidays] = useState<Holiday[] | null>(null);

  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    getLeaveForSelectedMonth(month + 1, year)
      .then((res) => {
        setHolidays(res.data as Holiday[]);
      })
      .catch((err) => {
        setError('Failed to load holidays');
        setHolidays([]);
        toast.error(`Failed to load holidays: ${error}`, {
          position: 'top-right',
          autoClose: 5000
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [month, year, dispatch]);

  const currentDate: string = moment().format('dddd, D MMMM YYYY');

  const handlePrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const handleToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  const handleClickOpen = () => {
    handleToday();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    handleToday();
    setModalHolidays(null);
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDayOfWeek = getFirstDayOfWeek(year, month);
  const prevMonthDays = firstDayOfWeek;

  const calendarRows: (number | null)[][] = [];
  let week: (number | null)[] = [];
  let day = 1 - prevMonthDays;
  while (day <= daysInMonth) {
    for (let i = 0; i < 7; i++) {
      if (day > 0 && day <= daysInMonth) {
        week.push(day);
      } else {
        week.push(null);
      }
      day++;
    }
    calendarRows.push(week);
    week = [];
  }

  const handleDayClick = (cellDay: number | null) => {
    if (!cellDay) return;
    const dateString = formatDate(year, month, cellDay);
    const holidaysForDay = getHolidaysForDate(dateString, holidays);
    if (holidaysForDay.length > 0) {
      setModalHolidays(holidaysForDay);
    }
  };

  const legendsToShow = getActiveLegends(
    holidays.filter((h) => {
      const date = new Date(h.eventDate);
      return date.getFullYear() === year && date.getMonth() === month;
    })
  );

  const renderHolidayModal = () => {
    if (!modalHolidays) return null;
    return (
      <Dialog
        open={!!modalHolidays}
        onClose={() => setModalHolidays(null)}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 6px 30px rgba(0,0,0,0.10)',
            backgroundColor: 'background.paper',
            p: 0,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            maxHeight: 460,
            minWidth: 310,
            maxWidth: 400,
            [theme.breakpoints.up('xl')]: {
              minWidth: 420,
              maxWidth: 520,
              maxHeight: 560
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            typography: 'h6',
            fontWeight: 600,
            position: 'relative',
            p: 1.5,
            pb: 1,
            backgroundColor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: { xs: '1rem', xl: '1.35rem' }
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ApartmentIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography
              noWrap
              sx={{
                fontSize: { xs: '1rem', xl: '1.15rem' },
                letterSpacing: '-0.2px',
                color: 'text.primary',
                fontWeight: 500,
                lineHeight: 1.2
              }}
            >
              {modalHolidays.length === 1
                ? modalHolidays[0].calenderType
                : 'Multiple Holidays'}
            </Typography>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={() => setModalHolidays(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.07)',
                color: 'grey.700'
              },
              p: 0.5
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 1.5,
            '&.MuiDialogContent-dividers': {
              borderTop: '1px solid',
              borderBottom: '1px solid',
              borderColor: 'divider'
            },
            fontSize: { xs: 13, xl: 16 }
          }}
        >
          <Stack spacing={1.5}>
            {modalHolidays.map((holiday, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: idx < modalHolidays.length - 1 ? 2 : 0,
                  p: 1,
                  background: holidayColors[holiday.calenderType] + '11',
                  borderRadius: 2,
                  border: `1px solid ${holidayColors[holiday.calenderType] + '44'}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                  <Typography
                    sx={{
                      fontSize: { xs: 13, xl: 15 },
                      fontWeight: 600,
                      color: holidayColors[holiday.calenderType] || '#1976d2'
                    }}
                  >
                    {holiday.calenderType}
                  </Typography>
                  {holiday.dayType === 1 && (
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      (Morning Half)
                    </Typography>
                  )}
                  {holiday.dayType === 2 && (
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      (Evening Half)
                    </Typography>
                  )}
                  {holiday.dayType === 0 && (
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      (Full Day)
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DescriptionIcon color="action" fontSize="small" sx={{ opacity: 0.8 }} />
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    fontWeight={500}
                    sx={{ minWidth: 42, fontSize: { xs: 12, xl: 15 } }}
                  >
                    Description:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      color: 'text.primary',
                      fontSize: { xs: 13, xl: 16 }
                    }}
                  >
                    {holiday.description}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EventIcon color="action" fontSize="small" sx={{ opacity: 0.8 }} />
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    fontWeight={500}
                    sx={{ minWidth: 42, fontSize: { xs: 12, xl: 15 } }}
                  >
                    Date:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      color: 'text.primary',
                      fontSize: { xs: 13, xl: 16 }
                    }}
                  >
                    {moment(holiday.eventDate).format('dddd, D MMMM YYYY')}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <Box
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: 'background.default',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Button
            onClick={() => setModalHolidays(null)}
            variant="text"
            sx={{
              borderRadius: '6px',
              px: 1.5,
              py: 0.3,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: 13, xl: 16 },
              minWidth: 0,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.05)'
              }
            }}
          >
            Done
          </Button>
        </Box>
      </Dialog>
    );
  };

  const getPaperSx = () => ({
    borderRadius: 3,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(60,60,60,0.18)',
    background: '#f9f9fb',
    width: {
      xs: '98vw',
      sm: '90vw',
      md: 380,
      lg: 420,
      xl: 670
    },
    height: {
      xs: '98vw',
      sm: '90vw',
      md: 380,
      lg: 420,
      xl: 670
    },
    maxWidth: '100vw',
    maxHeight: '100vh',
    minHeight: 0,
    minWidth: { xl: 540 },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    m: { xs: 'auto', sm: 'auto' },
    p: 0
  });

  return (
    <>
      <Tooltip arrow title="Calendar">
        <Box
          className="header-calendar-btn"
          onClick={handleClickOpen}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <CalendarMonthOutlinedIcon className="icon-header-calendar" />
          <Typography
            color="#092C4C"
            className="poppin-font calendar-text"
            sx={{ fontSize: 15, fontWeight: 500 }}
          >
            {currentDate}
          </Typography>
        </Box>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        maxWidth={false}
        fullWidth
        transitionDuration={400}
        PaperProps={{
          sx: getPaperSx()
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: 'none',
            width: '100%'
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            px={1.2}
            pt={1.2}
            pb={0.6}
          >
            <IconButton
              onClick={handlePrevMonth}
              size="small"
              sx={{
                bgcolor: '#f2f2f7',
                '&:hover': { bgcolor: '#e5e5ea' },
                borderRadius: 2,
                width: 32,
                height: 32
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography
              variant="h3"
              sx={{
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontWeight: 600,
                color: '#222',
                textAlign: 'center',
                fontSize: { xs: 20, xl: 32 }
              }}
            >
              {moment({ year, month }).format('MMM YYYY')}
            </Typography>
            <IconButton
              onClick={handleNextMonth}
              size="small"
              sx={{
                bgcolor: '#f2f2f7',
                '&:hover': { bgcolor: '#e5e5ea' },
                borderRadius: 2,
                width: 32,
                height: 32
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            pb={0.6}
          >
            <Button
              startIcon={<TodayIcon sx={{ fontSize: 18 }} />}
              onClick={handleToday}
              variant="contained"
              size="small"
              sx={{
                borderRadius: 8,
                background: '#007aff',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 500,
                px: 1.5,
                py: 0.2,
                fontSize: { xs: 13, xl: 18 },
                boxShadow: '0 1px 4px rgba(0,0,0,0.09)'
              }}
            >
              {moment(today).format('ddd, MMM D')}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            background: '#f9f9fb',
            flex: '1 1 auto',
            overflow: 'hidden',
            minHeight: 0,
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              flex: 1,
              alignItems: 'stretch',
              justifyContent: 'center',
              px: 0.5,
              pt: 1,
              pb: 0.5
            }}
          >
            <CalendarRoot sx={{ px: 0, pt: 0, pb: 0, flex: 1, minHeight: 0 }}>
              {loading ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{ minHeight: 180, width: '100%' }}
                >
                  <CircularProgress size={36} sx={{ color: '#007aff' }} />
                </Stack>
              ) : (
                <>
                  <CalendarTable>
                    <thead>
                      <tr>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                          (d) => (
                            <th
                              key={d}
                              style={{
                                fontWeight: 600,
                                padding: 4,
                                color: '#8e8e93',
                                fontSize: 12,
                                background: '#fff',
                                border: 'none'
                              }}
                            >
                              {d}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>

                    <tbody>
                      {calendarRows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cellDay, j) => {
                            if (!cellDay) {
                              return (
                                <CalendarCell
                                  key={j}
                                  isToday={false}
                                  style={{
                                    background: 'transparent',
                                    minWidth: 0,
                                    minHeight: 0,
                                    fontSize: 10
                                  }}
                                >
                                  &nbsp;
                                </CalendarCell>
                              );
                            }
                            const dateString = formatDate(year, month, cellDay);
                            const holidaysForDay = getHolidaysForDate(
                              dateString,
                              holidays
                            );
                            const isCurrentDay = isToday(year, month, cellDay);

                            const isSaturday = j === 6;
                            const isSunday = j === 0;

                            let cellBg: string | undefined;
                            let hasFullDayHoliday = false;
                            let showMorningHalf = false, showEveningHalf = false;

                            for (const holiday of holidaysForDay) {
                              if (holiday.dayType === 0) {
                                cellBg =
                                  holidayColors[holiday.calenderType] ||
                                  COMPANY_COLOR;
                                hasFullDayHoliday = true;
                              } else if (holiday.dayType === 1) {
                                showMorningHalf = true;
                              } else if (holiday.dayType === 2) {
                                showEveningHalf = true;
                              }
                            }

                            return (
                              <CalendarCell
                                key={j}
                                isToday={isCurrentDay}
                                onClick={() => handleDayClick(cellDay)}
                                sx={{
                                  borderRadius: '6px',
                                  border: isCurrentDay
                                    ? '2px solid #007aff'
                                    : 'none',
                                  background: cellBg
                                    ? cellBg + '22'
                                    : isCurrentDay
                                    ? '#fff'
                                    : isSunday || isSaturday
                                    ? '#f5f5f7'
                                    : 'transparent',
                                  color: cellBg
                                    ? cellBg
                                    : isCurrentDay
                                    ? '#007aff'
                                    : '#222',
                                  cursor: holidaysForDay.length > 0 ? 'pointer' : 'default',
                                  transition: 'background 0.2s',
                                  position: 'relative',
                                  minWidth: 0,
                                  minHeight: 0,
                                  textAlign: 'center',
                                  fontSize: { xs: 13, xl: 18 },
                                  fontWeight: holidaysForDay.length > 0 ? 600 : 400,
                                  boxShadow: isCurrentDay
                                    ? '0 2px 8px rgba(0,122,255,0.09)'
                                    : undefined,
                                  boxSizing: 'border-box',
                                  zIndex: isCurrentDay ? 1 : 'auto',
                                  '&:hover': {
                                    background: cellBg
                                      ? cellBg + '44'
                                      : '#e5e5ea'
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    pt: 0.6
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: holidaysForDay.length > 0 ? 700 : 400,
                                      color: isSunday
                                        ? '#e53935'
                                        : isSaturday
                                        ? '#1e88e5'
                                        : '#222',
                                      fontSize: { xs: 13, xl: 18 }
                                    }}
                                  >
                                    {cellDay}
                                  </Typography>
                                  {holidaysForDay.length > 0 && (
                                    <>
                                      {hasFullDayHoliday && (
                                        <Box
                                          sx={{
                                            width: 4,
                                            height: 4,
                                            borderRadius: '50%',
                                            background:
                                              cellBg || COMPANY_COLOR,
                                            position: 'absolute',
                                            left: '50%',
                                            bottom: 4,
                                            transform: 'translateX(-50%)'
                                          }}
                                        />
                                      )}
                                      {showMorningHalf && (
                                        <HalfdayDiv
                                          color={
                                            holidaysForDay.find(h => h.dayType === 1)
                                              ? (holidayColors[holidaysForDay.find(h => h.dayType === 1)!.calenderType] || COMPANY_COLOR)
                                              : COMPANY_COLOR
                                          }
                                          half="morning"
                                        />
                                      )}
                                      {showEveningHalf && (
                                        <HalfdayDiv
                                          color={
                                            holidaysForDay.find(h => h.dayType === 2)
                                              ? (holidayColors[holidaysForDay.find(h => h.dayType === 2)!.calenderType] || COMPANY_COLOR)
                                              : COMPANY_COLOR
                                          }
                                          half="evening"
                                        />
                                      )}
                                    </>
                                  )}
                                </Box>
                              </CalendarCell>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </CalendarTable>

                  {legendsToShow.length > 0 && (
                    <Legend
                      sx={{
                        mt: 1,
                        mb: 0.5,
                        px: 1,
                        position: 'static',
                        width: '100%',
                        justifyContent: 'center'
                      }}
                    >
                      {legendsToShow.map((legend) => (
                        <Box
                          key={legend.type}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mr={1}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: legend.color,
                              opacity: 1,
                              border: undefined,
                              position: 'relative'
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: legend.color,
                              fontWeight: 600,
                              fontSize: { xs: 11, xl: 14 }
                            }}
                          >
                            {legend.label}
                          </Typography>
                        </Box>
                      ))}
                    </Legend>
                  )}
                </>
              )}
            </CalendarRoot>
            {renderHolidayModal()}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Calendar;
